/**
 * 설정 메뉴 구조 — SettingsLayout 사이드바와 모바일 더보기가 같이 쓴다(같은 권한 검사).
 */
import React, { useMemo } from "react";
import { useMenuRoles } from "../../components/auth/MenuRoleContext";
import { buildAdminTree } from "../../data/menuAdminTree";
import { DEFAULT_CUSTOM_ICON, renderMenuIcon } from "../../data/menuIcons";
import { settingsMenuDef } from "../../data/settingsMenus";
import { useLanguage } from "../../components/ui/LanguageProvider";
import { SETTINGS_MENU_LABEL_EN } from "../../data/menuLabelsEn";

export type SettingsMenuItem = { key: string; label: string; icon: React.ReactNode; path: string; activePaths?: string[]; pageTitle?: string };
export type SettingsMenuSection = { key: string; label: string; items: SettingsMenuItem[] };

// 설정 = 한 곳. 메뉴 구조(이름·경로·순서·소속·추가)와 등급·노출은 메뉴 권한 관리 > 설정 메뉴 탭에서 정한다.
// 코드 카탈로그는 src/data/settingsMenus.ts, 트리는 CRM·IT 관리 화면과 같은 빌더(buildAdminTree "settings").

export function useSettingsSections(): SettingsMenuSection[] {
  const { overrides, visible } = useMenuRoles();
  const { language } = useLanguage();
  const L = (label: string) => (language === "en" ? SETTINGS_MENU_LABEL_EN[label] ?? label : label);
  return useMemo(() => {
    const root = buildAdminTree("settings", overrides);
    return root.children
      .map((section) => ({
        key: section.menuKey,
        label: L(section.label),
        items: section.children
          .filter((node) => node.effectivePath && visible(node.menuKey, node.defaultRole))
          .map((node) => ({
            key: node.menuKey,
            label: L(node.label),
            icon: renderMenuIcon(node.effectiveIcon || DEFAULT_CUSTOM_ICON, 16),
            path: node.effectivePath,
            activePaths: settingsMenuDef(node.menuKey)?.activePaths,
            pageTitle: node.pageTitleOverride || undefined,
          })),
      }))
      // 보이는 항목이 없는 섹션은 뺀다 — 섹션 자체 등급·노출은 항목 판정(상속)에 이미 들어 있다.
      .filter((section) => section.items.length > 0);
  }, [overrides, visible, language]); // eslint-disable-line
}

